import { Fragment, useEffect, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { Button } from '#/shared/ui/button'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '#/shared/ui/hover-card'
import { EditIcon } from '#/shared/ui/icons'
import { RichText } from '#/shared/ui/rich-text'
import { Skeleton } from '#/shared/ui/skeleton'
import { Textarea } from '#/shared/ui/textarea'
import { VoiceInputButton } from '#/shared/ui/voice-recording-island'

type NoteAlign = 'center' | 'start'

export interface NotePreviewItem {
  text: string
  date: string
  author: string
}

interface NoteCellProps {
  count?: number
  text?: string
  notes?: NotePreviewItem[]
  isLoading?: boolean
  onAddNote?: (text: string) => void
  editing?: boolean
  onEditingChange?: (editing: boolean) => void
  isSaving?: boolean
  align?: NoteAlign
}

const HOVER_OPEN_DELAY_MS = 120
const HOVER_CLOSE_DELAY_MS = 120

function useControlledEditing(
  controlled: boolean | undefined,
  onChange: ((editing: boolean) => void) | undefined,
): [boolean, (next: boolean) => void] {
  const [internal, setInternal] = useState(false)
  const isControlled = controlled !== undefined
  const editing = isControlled ? controlled : internal
  const setEditing = (next: boolean) => {
    if (!isControlled) setInternal(next)
    onChange?.(next)
  }
  return [editing, setEditing]
}

function NotePreviewDivider() {
  return <div className="h-px w-full bg-gray-200" />
}

function NoteItem({ note }: { note: NotePreviewItem }) {
  return (
    <div className="flex w-full flex-col gap-1.25">
      <RichText html={note.text} className="line-clamp-3 text-xs text-gray-900" />
      <div className="text-2xs flex items-center gap-1.25 text-gray-500">
        <span>{note.date}</span>
        <span>·</span>
        <span>{note.author}</span>
      </div>
    </div>
  )
}

function NoteItemSkeleton() {
  return (
    <div className="flex w-full flex-col gap-1.25">
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-2.5 w-24" />
    </div>
  )
}

function NotePreviewCard({
  notes,
  isLoading,
  onAddClick,
}: {
  notes: NotePreviewItem[]
  isLoading: boolean
  onAddClick: () => void
}) {
  const { t } = useTranslation()
  const items = isLoading ? [null, null] : notes
  return (
    <div className="flex max-h-112 w-60 flex-col items-end gap-2.5 p-2.5">
      <div className="flex min-h-0 w-full flex-col gap-2.5 overflow-y-auto">
        {items.map((note, i) => (
          <Fragment key={i}>
            {i > 0 && <NotePreviewDivider />}
            {note ? <NoteItem note={note} /> : <NoteItemSkeleton />}
          </Fragment>
        ))}
      </div>
      {items.length > 0 && <NotePreviewDivider />}
      <Button type="button" variant="outline-primary" size="xxs" onClick={onAddClick}>
        {t('table.note.addNote')}
      </Button>
    </div>
  )
}

function NoteEditorCard({
  initialValue = '',
  onCancel,
  onSubmit,
  isSaving = false,
}: {
  initialValue?: string
  onCancel: () => void
  onSubmit: (text: string) => void
  isSaving?: boolean
}) {
  const { t } = useTranslation()
  const [value, setValue] = useState(initialValue)
  const trimmed = value.trim()

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (isSaving) return
    if (event.key === 'Escape') {
      event.preventDefault()
      onCancel()
      return
    }
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && trimmed) {
      event.preventDefault()
      onSubmit(trimmed)
    }
  }

  return (
    <div className="w-72 p-2.5">
      <div className="flex flex-col items-end gap-2.5">
        <Textarea
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isSaving}
        />
        <div className="flex w-full items-center justify-between gap-1.5">
          <VoiceInputButton value={value} onChange={setValue} disabled={isSaving} />
          <div className="flex items-center gap-1.5">
            <Button
              type="button"
              variant="outline-primary"
              size="xxs"
              onClick={onCancel}
              disabled={isSaving}
            >
              {t('table.note.cancel')}
            </Button>
            <Button
              type="button"
              size="xxs"
              disabled={!trimmed || isSaving}
              onClick={() => onSubmit(trimmed)}
            >
              {isSaving ? t('table.saving') : t('table.note.addNote')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function EditableNoteText({
  text,
  onSubmit,
  editing,
  onEditingChange,
  isSaving,
}: {
  text: string
  onSubmit: (text: string) => void
  editing: boolean
  onEditingChange: (editing: boolean) => void
  isSaving: boolean
}) {
  const [hoverOpen, setHoverOpen] = useState(false)
  const editingRef = useRef(editing)
  editingRef.current = editing

  useEffect(() => {
    if (editing) setHoverOpen(false)
  }, [editing])

  return (
    <HoverCard
      openDelay={HOVER_OPEN_DELAY_MS}
      closeDelay={HOVER_CLOSE_DELAY_MS}
      open={editing || hoverOpen}
      onOpenChange={(next) => {
        if (editingRef.current) return
        setHoverOpen(next)
      }}
    >
      <HoverCardTrigger asChild>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onEditingChange(true)
          }}
          className="flex h-7 max-w-40 cursor-pointer items-center"
        >
          <span className="truncate text-sm text-gray-900">{text}</span>
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        align="center"
        side="bottom"
        sideOffset={4}
        collisionPadding={8}
        onEscapeKeyDown={() => {
          if (!isSaving) onEditingChange(false)
        }}
        onInteractOutside={() => {
          if (editing && !isSaving) onEditingChange(false)
        }}
      >
        {editing ? (
          <NoteEditorCard
            initialValue={text}
            onCancel={() => onEditingChange(false)}
            onSubmit={onSubmit}
            isSaving={isSaving}
          />
        ) : (
          <div className="w-60 p-2.5">
            <p className="text-xs whitespace-pre-wrap text-gray-900">{text}</p>
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  )
}

function NoteTrigger({
  count,
  onEdit,
  align,
}: {
  count: number | undefined
  onEdit: () => void
  align: NoteAlign
}) {
  return (
    <div
      className={cn(
        'relative flex h-7 items-center',
        align === 'center' ? 'justify-center' : 'justify-start',
      )}
    >
      {(count ?? 0) > 0 && (
        <span className="rounded-full px-0.75 text-sm text-gray-900">{count}</span>
      )}
      <button
        type="button"
        aria-label={(count ?? 0) > 0 ? 'Edit notes' : 'Add a note'}
        title={(count ?? 0) > 0 ? 'Edit notes' : 'Add a note'}
        onClick={(e) => {
          e.stopPropagation()
          onEdit()
        }}
        className="focus-visible:focus-ring flex cursor-pointer items-center rounded focus-visible:outline-none"
      >
        <EditIcon className="size-5 text-gray-900" />
      </button>
    </div>
  )
}

function NotePreview({
  count,
  notes,
  isLoading,
  onAddNote,
  editing,
  setEditing,
  isSaving,
  align,
}: {
  count: number | undefined
  notes: NotePreviewItem[]
  isLoading: boolean
  onAddNote: ((text: string) => void) | undefined
  editing: boolean
  setEditing: (next: boolean) => void
  isSaving: boolean
  align: NoteAlign
}) {
  const [hoverOpen, setHoverOpen] = useState(false)
  const editingRef = useRef(editing)
  editingRef.current = editing

  useEffect(() => {
    if (editing) setHoverOpen(false)
  }, [editing])

  const showPreviewContent = !editing && (isLoading || notes.length > 0)
  const open = editing || (hoverOpen && showPreviewContent)

  return (
    <HoverCard
      openDelay={HOVER_OPEN_DELAY_MS}
      closeDelay={HOVER_CLOSE_DELAY_MS}
      open={open}
      onOpenChange={(next) => {
        if (editingRef.current) return
        setHoverOpen(next)
      }}
    >
      <HoverCardTrigger asChild>
        <div>
          <NoteTrigger count={count} onEdit={() => setEditing(true)} align={align} />
        </div>
      </HoverCardTrigger>
      <HoverCardContent
        align="center"
        side="bottom"
        sideOffset={4}
        collisionPadding={8}
        onEscapeKeyDown={() => {
          if (!isSaving) setEditing(false)
        }}
        onInteractOutside={() => {
          if (editing && !isSaving) setEditing(false)
        }}
      >
        {editing ? (
          <NoteEditorCard
            onCancel={() => setEditing(false)}
            onSubmit={(value) => onAddNote?.(value)}
            isSaving={isSaving}
          />
        ) : (
          <NotePreviewCard
            notes={notes}
            isLoading={isLoading}
            onAddClick={() => setEditing(true)}
          />
        )}
      </HoverCardContent>
    </HoverCard>
  )
}

export function NoteCell({
  count,
  text,
  notes = [],
  isLoading = false,
  onAddNote,
  editing: controlledEditing,
  onEditingChange,
  isSaving = false,
  align = 'center',
}: NoteCellProps) {
  const [editing, setEditing] = useControlledEditing(controlledEditing, onEditingChange)

  if (text) {
    if (!onAddNote) {
      return (
        <div className="flex h-7 items-center">
          <span className="text-sm text-gray-900">{text}</span>
        </div>
      )
    }
    return (
      <EditableNoteText
        text={text}
        onSubmit={onAddNote}
        editing={editing}
        onEditingChange={setEditing}
        isSaving={isSaving}
      />
    )
  }

  return (
    <NotePreview
      count={count}
      notes={notes}
      isLoading={isLoading}
      onAddNote={onAddNote}
      editing={editing}
      setEditing={setEditing}
      isSaving={isSaving}
      align={align}
    />
  )
}
